import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export type GuideKey = 'setup' | 'payments';
export type PickupDropHintStage = 'pick-kid' | 'add-hub' | null;
export type BillingHintStage = 'pick-kid' | 'set-price' | null;

/**
 * There's a single floating guide widget slot (bottom-right). Only one
 * guide shows at a time — starting one implicitly dismisses the other,
 * since both widgets react to the same activeGuide value.
 */
@Injectable({ providedIn: 'root' })
export class GuideWidgetStateService {
  readonly activeGuide$ = new BehaviorSubject<GuideKey | null>(null);

  /** Which guide's history chain the current page belongs to. Used by
   *  navigateForGuide to tell the first hop of a guide session (push, so
   *  the originating page stays a real Back target) from later hops in
   *  the same session (replace, so they collapse into one entry). */
  private anchoredGuide: GuideKey | null = null;

  /**
   * Staged contextual driver.js hint for the "Add Pickup & Drop" guide step,
   * which spans two pages (pick a kid on the list, then click Add on that
   * kid's detail page). 'pick-kid' fires the list-page hint and advances to
   * 'add-hub'; the detail page consumes 'add-hub' and clears it.
   */
  pickupDropHintStage: PickupDropHintStage = null;

  advancePickupDropHint(): void {
    if (this.pickupDropHintStage === 'pick-kid') this.pickupDropHintStage = 'add-hub';
  }

  clearPickupDropHint(): void {
    this.pickupDropHintStage = null;
  }

  /**
   * Staged contextual hint for "Add payment for a kid", which also spans
   * two pages: 'pick-kid' fires the list-page hint and advances to
   * 'set-price'; Kid Detail consumes 'set-price' by pointing at the first
   * hub-link's "Set price" button (only rendered once the kid has at
   * least one hub link with no plan yet — see kid-detail.page.html's
   * Billing card) and clears.
   */
  billingHintStage: BillingHintStage = null;

  advanceBillingHint(): void {
    if (this.billingHintStage === 'pick-kid') this.billingHintStage = 'set-price';
  }

  clearBillingHint(): void {
    this.billingHintStage = null;
  }

  /** Staged contextual hint for "Connect Stripe account" — points at the
   *  "Stripe Payments" row on the general Settings page so the user knows
   *  what to click next instead of landing on a plain list. */
  stripeConnectHintPending = false;

  requestStripeConnectHint(): void {
    this.stripeConnectHintPending = true;
  }

  consumeStripeConnectHint(): boolean {
    const pending = this.stripeConnectHintPending;
    this.stripeConnectHintPending = false;
    return pending;
  }

  /** Single-page contextual hint for "Choose who pays the fees" — points at
   *  the "Processing fees" row on the payments settings page. */
  processingFeesHintPending = false;

  requestProcessingFeesHint(): void {
    this.processingFeesHintPending = true;
  }

  consumeProcessingFeesHint(): boolean {
    const pending = this.processingFeesHintPending;
    this.processingFeesHintPending = false;
    return pending;
  }

  showSetupGuide(): void {
    this.activeGuide$.next('setup');
  }

  showPaymentsGuide(): void {
    this.activeGuide$.next('payments');
  }

  /** No-op unless `guide` is the one currently showing — avoids one
   *  widget's dismiss accidentally clearing the OTHER guide's active state. */
  hideGuide(guide: GuideKey): void {
    if (this.activeGuide$.value === guide) {
      this.activeGuide$.next(null);
      this.anchoredGuide = null;
    }
  }

  /**
   * Navigate to a guide step. The first hop of a guide session pushes a
   * normal history entry — the page the guide was opened from (usually
   * Dashboard) stays a real Back target. Every subsequent hop within the
   * *same* guide session replaces the current entry instead, so no matter
   * how many steps are visited via the guide, Back always returns to that
   * same originating page in one click.
   */
  navigateForGuide(
    router: Router,
    guide: GuideKey,
    route: string,
    options?: {
      pickupDropHint?: boolean;
      stripeConnectHint?: boolean;
      billingHint?: boolean;
      processingFeesHint?: boolean;
    },
  ): void {
    const isFirstHopThisSession = this.anchoredGuide !== guide;
    this.anchoredGuide = guide;
    if (options?.pickupDropHint) this.pickupDropHintStage = 'pick-kid';
    if (options?.stripeConnectHint) this.stripeConnectHintPending = true;
    if (options?.billingHint) this.billingHintStage = 'pick-kid';
    if (options?.processingFeesHint) this.processingFeesHintPending = true;
    router.navigateByUrl(route, { replaceUrl: !isFirstHopThisSession });
  }
}
